import { router } from '@inertiajs/react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { EmailFilters as EmailFiltersType } from '@/types';

export function EmailFilters({
  initialParams,
}: {
  initialParams: EmailFiltersType;
}) {
  const [search, setSearch] = useState(initialParams.search || '');
  const [status, setStatus] = useState<string>(initialParams.status || 'all');
  const [sortBy, setSortBy] = useState(initialParams.sortBy);
  const [sortOrder, setSortOrder] = useState(initialParams.sortOrder);

  useEffect(() => {
    setSearch(initialParams.search || '');
    setStatus(initialParams.status || 'all');
    setSortBy(initialParams.sortBy);
    setSortOrder(initialParams.sortOrder);
  }, [
    initialParams.search,
    initialParams.status,
    initialParams.sortBy,
    initialParams.sortOrder,
  ]);

  const applyFilters = () => {
    const params: Record<string, string> = {
      page: '1',
      sortBy,
      sortOrder,
    };

    if (search) params.search = search;
    if (status && status !== 'all') params.status = status;

    router.get('/admin/emails', params);
  };

  const clearFilters = () => {
    setSearch('');
    setStatus('all');
    setSortBy('createdAt');
    setSortOrder('desc');
    router.get('/admin/emails');
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-2">
          <Label htmlFor="search">Search</Label>
          <Input
            id="search"
            placeholder="Subject, recipient, content..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                applyFilters();
              }
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sortBy">Sort By</Label>
          <Select
            value={sortBy}
            onValueChange={(value) =>
              setSortBy(value as EmailFiltersType['sortBy'])
            }
          >
            <SelectTrigger id="sortBy">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="createdAt">Created Date</SelectItem>
              <SelectItem value="sentAt">Sent Date</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sortOrder">Order</Label>
          <Select
            value={sortOrder}
            onValueChange={(value) =>
              setSortOrder(value as EmailFiltersType['sortOrder'])
            }
          >
            <SelectTrigger id="sortOrder">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Newest First</SelectItem>
              <SelectItem value="asc">Oldest First</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={applyFilters}>Apply Filters</Button>
        <Button variant="outline" onClick={clearFilters}>
          Clear Filters
        </Button>
      </div>
    </div>
  );
}
